import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { api } from '@/api';
import { toast } from 'sonner';
import { AlertCircle, FileText, BarChart3, Trash2, Edit2, Loader2, Filter, X } from 'lucide-react';
import ProfessionalTeamManager from '@/components/ProfessionalTeamManager';
import InternalTeamManager from '@/components/InternalTeamManager';
import RFIList from '@/components/RFIList';
import RFIForm from '@/components/RFIForm';
import SIList from '@/pages/SIList';
import ProgramManager from '@/components/ProgramManager';
import SubContractorManager from '@/components/SubContractorManager';
import DrawingRegister from '@/components/DrawingRegister';
import MilestoneDatesModule from '@/components/MilestoneDatesModule';
import { WalkthroughTrigger } from '@/components/walkthrough';
import { useAuth } from '@/context/AuthContext';
import { useProject } from '@/context/ProjectContext';
import { usePermissions } from '@/hooks/usePermissions';
import { 
  ProjectOverviewTab, 
  AwaitingItemsTab, 
  NoticesTab, 
  ClaimsTab,
  VariationOrdersTab,
  MeetingMinutesTab,
  ShopDrawingsTab
} from '@/components/project-tabs';
import DelayEventsTab from '@/components/project-tabs/DelayEventsTab';
import ProjectTabBar from '@/components/project/ProjectTabBar';
import EditProjectDialog from '@/components/project/EditProjectDialog';
import EditContractRulesDialog from '@/components/project/EditContractRulesDialog';

// Filter banner component
const FilterBanner = ({ filterName, count, onClear, color = 'blue' }) => {
  const colors = {
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    purple: 'bg-purple-50 border-purple-200 text-purple-800',
    red: 'bg-red-50 border-red-200 text-red-800',
    teal: 'bg-teal-50 border-teal-200 text-teal-800'
  };
  
  return (
    <div className={`flex items-center justify-between p-3 ${colors[color]} border rounded-lg mb-4`}>
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4" />
        <span className="text-sm font-medium">
          Filtered by: <span className="font-bold">{filterName}</span>
        </span>
        <Badge variant="secondary" className="text-xs">{count} items</Badge>
      </div>
      <Button variant="ghost" size="sm" onClick={onClear} className="hover:bg-white/50">
        <X className="h-4 w-4 mr-1" /> Clear Filter
      </Button>
    </div>
  );
};

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { setSelectedProjectId } = useProject();
  const permissions = usePermissions();
  const { canCreateEvents, canCreateRFI, canCreateSI, canManageTeams, isAdmin } = permissions;
  
  // Module tab visibility based on permissions
  const canViewModule = (module) => permissions.can(module, 'view');
  const [project, setProject] = useState(null);
  const [events, setEvents] = useState([]);
  const [claims, setClaims] = useState([]); // Direct claims from eot_claims collection
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [rfiFormOpen, setRfiFormOpen] = useState(false);
  
  // Professional and Internal team for dropdowns
  const [professionalTeam, setProfessionalTeam] = useState([]);
  const [internalTeam, setInternalTeam] = useState([]);
  
  // Data for new inline tabs
  const [variationOrders, setVariationOrders] = useState([]);
  const [meetingMinutes, setMeetingMinutes] = useState([]);
  const [shopDrawings, setShopDrawings] = useState([]);
  const [selectedVO, setSelectedVO] = useState(null);
  const [selectedMinute, setSelectedMinute] = useState(null);
  const [selectedShopDrawing, setSelectedShopDrawing] = useState(null);
  
  const [clauseOptions, setClauseOptions] = useState([]);
  const [contractRules, setContractRules] = useState(null);
  const [editContractDialogOpen, setEditContractDialogOpen] = useState(false);
  const [savingRules, setSavingRules] = useState(false);
  const [pendingRules, setPendingRules] = useState(null);
  const [editProjectOpen, setEditProjectOpen] = useState(false);
  const [editProjectData, setEditProjectData] = useState({});
  const [savingProject, setSavingProject] = useState(false);

  // Handle tab and filter query parameters from URL
  const [urlFilter, setUrlFilter] = useState('');
  
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    const filterParam = searchParams.get('filter');
    
    // Set filter from URL
    if (filterParam) {
      setUrlFilter(filterParam);
    }
    
    if (tabParam) {
      // Redirect procurement and IRS to their full pages
      const lower = tabParam.toLowerCase();
      if (lower === 'procurement') {
        navigate(`/projects/${projectId}/procurement`, { replace: true });
        return;
      }
      if (lower === 'irs') {
        navigate(`/projects/${projectId}/irs`, { replace: true });
        return;
      }
      if (lower === 'shop-drawings' || lower === 'shop_drawings') {
        navigate(`/projects/${projectId}/shop-drawings`, { replace: true });
        return;
      }
      if (lower === 'minutes' || lower === 'meeting-minutes') {
        navigate(`/projects/${projectId}/meeting-minutes`, { replace: true });
        return;
      }

      // Map URL tab names to internal tab names
      const tabMap = {
        'overview': 'overview',
        'events': 'events',
        'notices': 'notices',
        'claims': 'claims',
        'rfis': 'rfi',
        'rfi': 'rfi',
        'si': 'si',
        'drawings': 'drawings',
        'programs': 'programs',
        'professional': 'professional',
        'internal': 'internal',
        'subcontractors': 'subcontractors',
        'milestones': 'milestones',
        'awaiting': 'awaiting'
      };
      const mappedTab = tabMap[lower];
      if (mappedTab) {
        setActiveTab(mappedTab);
      }
    }
  }, [location.search]);

  // Awaiting items state
  const [awaitingItems, setAwaitingItems] = useState([]);
  const [awaitingLoading, setAwaitingLoading] = useState(false);
  const [awaitingModuleFilter, setAwaitingModuleFilter] = useState('all');
  const [awaitingPriorityFilter, setAwaitingPriorityFilter] = useState('all');
  const [awaitingRefreshKey, setAwaitingRefreshKey] = useState(0);

  // Filter states for Notices, Claims
  const [noticeSearch, setNoticeSearch] = useState('');
  const [noticeStatusFilter, setNoticeStatusFilter] = useState('all');
  
  const [claimSearch, setClaimSearch] = useState('');
  const [claimStatusFilter, setClaimStatusFilter] = useState('all');

  // Milestone management
  const [milestoneDialogOpen, setMilestoneDialogOpen] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [milestoneForm, setMilestoneForm] = useState({
    date: '',
    title: '',
    description: ''
  });

  const canEditTeam = user?.role === 'admin' || user?.role === 'contracts_manager';

  // Project summary state for overview tab
  const [projectSummary, setProjectSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const loadProjectSummary = useCallback(async () => {
    if (!projectId) return;
    setSummaryLoading(true);
    try {
      const [summaryRes, shopDrawingsRes, meetingMinutesRes, procurementRes, irsRes, programsRes, subcontractorsRes, periodicRes, diaryRes, plantRes] = await Promise.all([
        api.get(`/dashboard/projects/${projectId}/summary?days=30`).catch(() => ({ data: null })),
        api.get(`/projects/${projectId}/shop-drawings/stats`).catch(() => ({ data: null })),
        api.get(`/meeting-minutes/stats/${projectId}`).catch(() => ({ data: null })),
        api.get(`/projects/${projectId}/procurement/stats`).catch(() => ({ data: null })),
        api.get(`/projects/${projectId}/irs/stats`).catch(() => ({ data: null })),
        api.get(`/projects/${projectId}/programs`).catch(() => ({ data: null })),
        api.get(`/projects/${projectId}/subcontractors`).catch(() => ({ data: [] })),
        api.get(`/periodic-reports/dashboard/${projectId}`).catch(() => ({ data: null })),
        api.get(`/diary/projects/${projectId}`).catch(() => ({ data: { diaries: [] } })),
        api.get(`/plant/register?project_id=${projectId}`).catch(() => ({ data: { items: [] } }))
      ]);
      
      // Map the KPIs to the expected structure
      const kpis = summaryRes.data?.kpis || {};
      const programsList = programsRes.data?.programs || [];
      const programStats = programsRes.data?.stats || {};
      const subcontractorsList = Array.isArray(subcontractorsRes.data) ? subcontractorsRes.data : [];
      const milestones = project?.key_milestones || [];
      const milestonesUpcoming = milestones.filter(m => {
        const d = m.date ? new Date(m.date) : null;
        return d && d > new Date();
      }).length;
      const milestonesPast = milestones.length - milestonesUpcoming;
      setProjectSummary({
        project: summaryRes.data?.project,
        period: summaryRes.data?.period,
        si: kpis.site_instructions || { total: 0, open: 0, overdue: 0 },
        rfis: kpis.rfis || { total: 0, open: 0, overdue: 0 },
        events: kpis.delay_events || { total: 0, open: 0, draft: 0 },
        notices: kpis.notices || { total: 0, submitted: 0, overdue: 0 },
        claims: kpis.claims || { total: 0, submitted: 0, pending: 0 },
        vos: kpis.variation_orders || { total: 0, pending: 0, approved: 0 },
        drawings: kpis.drawings || { total: 0, recent: 0, pending: 0 },
        tasks: kpis.tasks || { total: 0, open: 0, overdue: 0 },
        shopDrawings: {
          ...(kpis.shop_drawings || { total: 0, by_status: {}, under_review: 0, approved: 0, rejected: 0 }),
          ...(shopDrawingsRes.data || {}),
          total: kpis.shop_drawings?.total ?? shopDrawingsRes.data?.total_packages ?? 0,
          under_review: kpis.shop_drawings?.under_review ?? 0,
          approved: kpis.shop_drawings?.approved ?? 0,
          rejected: kpis.shop_drawings?.rejected ?? 0,
        },
        programs: { total: programsList.length, analyzed: programStats.analyzed || 0, pending: programStats.pending || 0 },
        meetingMinutes: meetingMinutesRes.data,
        procurement: procurementRes.data,
        irs: irsRes.data,
        milestones: { total: milestones.length, upcoming: milestonesUpcoming, past: milestonesPast },
        subcontractors: { total: subcontractorsList.length, active: subcontractorsList.filter(s => s.status === 'active' || !s.status).length, onSite: subcontractorsList.filter(s => s.status === 'on_site').length },
        periodicReports: periodicRes.data || { total_cycles: 0, reports_submitted: 0, reports_outstanding: 0, reports_overdue: 0 },
        siteDiary: (() => {
          const diaries = diaryRes.data?.diaries || [];
          const total = diaries.length;
          const completed = diaries.filter(d => d.status === 'submitted' || d.status === 'approved').length;
          const draft = diaries.filter(d => d.status === 'draft').length;
          return { total, completed, draft };
        })(),
        plant: (() => {
          const items = plantRes.data?.items || [];
          const total = items.length;
          const active = items.filter(i => i.status === 'On Site' || i.status === 'Active' || i.status === 'active').length;
          const breakdown = items.filter(i => i.status === 'Breakdown' || i.status === 'breakdown').length;
          return { total, active, breakdown };
        })()
      });
    } catch (error) {
      console.error('Failed to load project summary:', error);
    } finally {
      setSummaryLoading(false);
    }
  }, [projectId]);

  // Load summary when on overview tab
  useEffect(() => {
    if (activeTab === 'overview') {
      loadProjectSummary();
    }
  }, [activeTab, loadProjectSummary]);

  // Load awaiting items when tab is active (overview or awaiting)
  useEffect(() => {
    if ((activeTab === 'awaiting' || activeTab === 'overview') && projectId) {
      const loadAwaitingItems = async () => {
        setAwaitingLoading(true);
        try {
          // Load all items that require attention in parallel
          const [siRes, rfiRes, tasksRes, shopDrawingsRes, irsRes, procurementRes, noticesRes, eventsRes, claimsRes, meetingMinutesRes] = await Promise.all([
            api.get(`/projects/${projectId}/site-instructions`).catch(() => ({ data: [] })),
            api.get(`/projects/${projectId}/rfis`).catch(() => ({ data: { items: [] } })),
            api.get(`/projects/${projectId}/tasks`).catch(() => ({ data: [] })),
            api.get(`/projects/${projectId}/shop-drawings`).catch(() => ({ data: { items: [] } })),
            api.get(`/projects/${projectId}/irs`).catch(() => ({ data: { items: [] } })),
            api.get(`/projects/${projectId}/procurement`).catch(() => ({ data: [] })),
            api.get(`/projects/${projectId}/notices`).catch(() => ({ data: [] })),
            api.get(`/projects/${projectId}/delay-events`).catch(() => ({ data: [] })),
            api.get(`/projects/${projectId}/claims`).catch(() => ({ data: [] })),
            api.get(`/meeting-minutes/project/${projectId}`).catch(() => ({ data: [] }))
          ]);

          // Combine and normalize all awaiting items
          const allItems = [];

          // Helper: calculate days remaining using SAST (UTC+2) midnight-to-midnight
          const daysRemaining = (dateStr) => {
            if (!dateStr) return null;
            // Get current date in SAST (UTC+2)
            const now = new Date();
            const sastOffset = 2 * 60; // SAST is UTC+2
            const utcMs = now.getTime() + (now.getTimezoneOffset() * 60000);
            const sastNow = new Date(utcMs + (sastOffset * 60000));
            const todaySAST = new Date(sastNow.getFullYear(), sastNow.getMonth(), sastNow.getDate());
            
            // Parse deadline and get its date portion in SAST
            const deadline = new Date(dateStr);
            const deadlineUtcMs = deadline.getTime() + (deadline.getTimezoneOffset() * 60000);
            const deadlineSAST = new Date(deadlineUtcMs + (sastOffset * 60000));
            const deadlineDateSAST = new Date(deadlineSAST.getFullYear(), deadlineSAST.getMonth(), deadlineSAST.getDate());
            
            const diff = Math.round((deadlineDateSAST - todaySAST) / (1000 * 60 * 60 * 24));
            return diff;
          };

          // Helper: check if a date is overdue using SAST
          const isOverdueSAST = (dateStr) => {
            if (!dateStr) return false;
            return daysRemaining(dateStr) < 0;
          };

          // Site Instructions awaiting clarification or pending approval
          const siItems = Array.isArray(siRes.data) ? siRes.data : (siRes.data?.items || []);
          siItems.forEach(item => {
            if (item.status === 'awaiting_clarification' || item.status === 'Awaiting Clarification' || 
                item.status === 'Pending Approval' || item.status === 'pending_approval' ||
                item.status === 'open' || item.status === 'Open') {
              allItems.push({
                id: item.id,
                type: 'site_instruction',
                typeLabel: 'Site Instruction',
                title: item.subject || item.title || `SI-${item.si_number}`,
                description: item.instruction || item.description,
                status: item.status === 'Pending Approval' || item.status === 'pending_approval' ? 'Pending Approval' : 
                        item.status === 'open' || item.status === 'Open' ? 'Open' : 'Awaiting Clarification',
                assignee: item.contractor_company || 'Contractor',
                submittedBy: item.issued_by || item.created_by_name || 'Project Manager',
                toWhom: item.contractor_company || 'Contractor',
                dueDate: item.response_due_date,
                createdAt: item.issue_date || item.created_at,
                daysLeft: daysRemaining(item.response_due_date),
                responsePeriod: item.response_period || null,
                url: `/projects/${projectId}/si/${item.id}`,
                priority: item.is_critical ? 'critical' : 'normal',
                icon: 'FileText'
              });
            }
          });

          // RFIs awaiting response - include ALL that are not closed/completed
          const rfiItems = Array.isArray(rfiRes.data) ? rfiRes.data : (rfiRes.data?.items || []);
          rfiItems.forEach(item => {
            const status = item.status?.toLowerCase();
            // Include all RFIs that are not closed, responded, or completed
            if (status !== 'closed' && status !== 'responded' && status !== 'completed' && status !== 'cancelled') {
              const isOverdue = isOverdueSAST(item.response_due_date);
              allItems.push({
                id: item.id,
                type: 'rfi',
                typeLabel: 'RFI',
                title: item.subject || `RFI-${item.rfi_number}`,
                description: item.question || item.description,
                status: status === 'draft' ? 'Draft' : 'Awaiting Response',
                assignee: item.addressed_to || 'Consultant',
                submittedBy: item.initiated_by || item.raised_by || item.created_by_name || 'Project Team',
                toWhom: item.addressed_to || 'Consultant',
                dueDate: item.response_due_date || item.required_date,
                createdAt: item.created_at || item.issue_date,
                daysLeft: daysRemaining(item.response_due_date || item.required_date),
                responsePeriod: item.response_period || null,
                url: `/projects/${projectId}/rfi/${item.id}`,
                priority: isOverdue ? 'overdue' : (item.is_critical ? 'critical' : 'normal'),
                icon: 'FileQuestion'
              });
            }
          });

          // Tasks that are open/overdue/in_progress
          const taskItems = Array.isArray(tasksRes.data) ? tasksRes.data : (tasksRes.data?.items || []);
          taskItems.forEach(item => {
            const status = item.status?.toLowerCase();
            if (status === 'open' || status === 'in_progress' || status === 'overdue' || status === 'pending') {
              const isOverdue = isOverdueSAST(item.due_date);
              allItems.push({
                id: item.id,
                type: 'task',
                typeLabel: 'Task',
                title: item.title || item.name,
                description: item.description,
                status: isOverdue ? 'Overdue' : (status === 'in_progress' ? 'In Progress' : 'Open'),
                assignee: item.assigned_to || item.responsible_party || 'Unassigned',
                submittedBy: item.created_by_name || item.created_by || 'Project Team',
                toWhom: item.assigned_to || item.responsible_party || 'Unassigned',
                dueDate: item.due_date,
                createdAt: item.created_at,
                daysLeft: daysRemaining(item.due_date),
                url: `/projects/${projectId}?tab=tasks&taskId=${item.id}`,
                priority: isOverdue ? 'overdue' : (item.priority === 'high' ? 'critical' : 'normal'),
                icon: 'CheckSquare'
              });
            }
          });

          // Shop Drawings pending review
          const sdItems = Array.isArray(shopDrawingsRes.data) ? shopDrawingsRes.data : (shopDrawingsRes.data?.items || []);
          sdItems.forEach(item => {
            const status = item.status?.toLowerCase();
            if (status?.includes('pending') || status?.includes('submitted') || status?.includes('draft') || status === 'with_consultant') {
              const isOverdue = isOverdueSAST(item.due_date);
              allItems.push({
                id: item.id,
                type: 'shop_drawing',
                typeLabel: 'Shop Drawing',
                title: item.package_name || item.title || `SD-${item.id?.slice(0,8)}`,
                description: item.description || `${item.discipline || ''} - ${item.trade || ''}`.trim(),
                status: status === 'draft' ? 'Draft' : 'Pending Review',
                assignee: item.consultant || 'Consultant',
                submittedBy: item.subcontractor || item.created_by_name || 'Subcontractor',
                toWhom: item.consultant || 'Consultant',
                dueDate: item.due_date || item.response_due_date,
                createdAt: item.created_at || item.submitted_date,
                daysLeft: daysRemaining(item.due_date || item.response_due_date),
                url: `/shop-drawings/${item.id}`,
                priority: isOverdue ? 'overdue' : 'normal',
                icon: 'FileImage'
              });
            }
          });

          // IRS items not yet requested, requested, or late
          const irsItemsData = Array.isArray(irsRes.data) ? irsRes.data : (irsRes.data?.items || []);
          irsItemsData.forEach(item => {
            // Include not_requested, requested, and late items as "awaiting"
            if (item.status === 'not_requested' || item.status === 'requested' || item.status === 'late') {
              allItems.push({
                id: item.id,
                type: 'irs',
                typeLabel: 'Info Required',
                title: item.description || `IRS Item`,
                description: `${item.discipline?.replace('_', ' ') || ''} - ${item.consultant || 'Consultant'}`.trim(),
                status: item.status === 'late' ? 'Late' : (item.status === 'not_requested' ? 'Not Requested' : 'Requested'),
                assignee: item.consultant || 'Consultant',
                submittedBy: 'Project Team',
                toWhom: item.consultant || 'Consultant',
                dueDate: item.date_information_required,
                createdAt: item.created_at,
                daysLeft: daysRemaining(item.date_information_required),
                url: `/projects/${projectId}/irs?item=${item.id}`,
                priority: item.status === 'late' || item.delay_flag ? 'overdue' : (item.critical_flag ? 'critical' : 'normal'),
                icon: 'ClipboardList'
              });
            }
          });

          // Procurement items - include items that need action or are approaching required date
          const procItems = Array.isArray(procurementRes.data) ? procurementRes.data : (procurementRes.data?.items || []);
          // Use SAST-aware date for procurement overdue/close-to-required checks
          const nowSAST = new Date();
          const sastOffsetMs = 2 * 60 * 60000;
          const utcNowMs = nowSAST.getTime() + (nowSAST.getTimezoneOffset() * 60000);
          const todaySAST = new Date(utcNowMs + sastOffsetMs);
          todaySAST.setHours(0, 0, 0, 0);
          const twoWeeksFromNow = new Date(todaySAST.getTime() + 14 * 24 * 60 * 60 * 1000);
          procItems.forEach(item => {
            const status = item.status?.toLowerCase();
            const requiredDate = item.required_date ? new Date(item.required_date) : null;
            const isCloseToRequired = requiredDate && requiredDate <= twoWeeksFromNow && requiredDate >= todaySAST;
            const isOverdue = requiredDate && requiredDate < todaySAST;
            
            // Include items that are pending, awaiting quote, close to required date, or overdue
            if (status === 'pending' || status === 'awaiting_quote' || !status || 
                isCloseToRequired || isOverdue || item.delay_flag) {
              allItems.push({
                id: item.id,
                type: 'procurement',
                typeLabel: 'Procurement',
                title: item.package_name || item.description || item.item_name || `Procurement Item`,
                description: `${item.trade || item.category || ''} - ${item.subcontractor || 'Subcontractor'}`.trim(),
                status: isOverdue ? 'Overdue' : (isCloseToRequired ? 'Due Soon' : 'Pending'),
                assignee: item.subcontractor || 'Subcontractor',
                submittedBy: 'Project Team',
                toWhom: item.subcontractor || 'Subcontractor',
                dueDate: item.required_date || item.delivery_planned,
                createdAt: item.created_at,
                daysLeft: daysRemaining(item.required_date || item.delivery_planned),
                url: `/projects/${projectId}/procurement?item=${item.id}`,
                priority: isOverdue || item.delay_flag ? 'overdue' : (isCloseToRequired ? 'critical' : 'normal'),
                icon: 'Truck'
              });
            }
          });

          // Notices - draft or submitted notices needing action
          const noticeItems = Array.isArray(noticesRes.data) ? noticesRes.data : (noticesRes.data?.items || []);
          noticeItems.forEach(item => {
            const status = item.status?.toLowerCase();
            if (status === 'draft' || status === 'submitted' || status === 'pending' || status === 'open') {
              const isOverdue = isOverdueSAST(item.response_due_date);
              allItems.push({
                id: item.id,
                type: 'notice',
                typeLabel: 'Notice',
                title: item.title || item.subject || `Notice-${item.notice_number || item.id?.slice(0,8)}`,
                description: item.description || item.notice_type,
                status: status === 'draft' ? 'Draft' : (isOverdue ? 'Overdue' : 'Submitted'),
                assignee: item.responsible_party || item.addressed_to || 'Contractor',
                submittedBy: item.created_by_name || item.issued_by || 'Project Team',
                toWhom: item.responsible_party || item.addressed_to || 'Principal Agent',
                dueDate: item.response_due_date || item.due_date,
                createdAt: item.created_at || item.notice_date,
                daysLeft: daysRemaining(item.response_due_date || item.due_date),
                url: `/notices/${item.id}`,
                priority: isOverdue ? 'overdue' : (status === 'draft' ? 'normal' : 'normal'),
                icon: 'Bell'
              });
            }
          });

          // Delay Events - open or pending events
          const eventItems = Array.isArray(eventsRes.data) ? eventsRes.data : (eventsRes.data?.items || []);
          eventItems.forEach(item => {
            const status = item.status?.toLowerCase();
            if (status === 'draft' || status === 'open' || status === 'pending' || status === 'under_review') {
              allItems.push({
                id: item.id,
                type: 'delay_event',
                typeLabel: 'Delay Event',
                title: item.title || item.event_description || `Event-${item.event_number || item.id?.slice(0,8)}`,
                description: item.description || item.cause,
                status: status === 'draft' ? 'Draft' : (status === 'under_review' ? 'Under Review' : 'Open'),
                assignee: item.responsible_party || 'Project Team',
                submittedBy: item.reported_by || item.created_by_name || 'Project Team',
                toWhom: item.responsible_party || 'Principal Agent',
                dueDate: item.response_due_date,
                createdAt: item.created_at || item.event_date,
                daysLeft: daysRemaining(item.response_due_date),
                url: `/events/${item.id}`,
                priority: item.is_critical ? 'critical' : 'normal',
                icon: 'AlertTriangle'
              });
            }
          });

          // Claims - draft or submitted claims
          const claimItems = Array.isArray(claimsRes.data) ? claimsRes.data : (claimsRes.data?.items || []);
          claimItems.forEach(item => {
            const status = item.status?.toLowerCase();
            if (status === 'draft' || status === 'submitted' || status === 'pending' || status === 'under_review') {
              allItems.push({
                id: item.id,
                type: 'claim',
                typeLabel: 'Claim',
                title: item.title || item.claim_description || `Claim-${item.claim_number || item.id?.slice(0,8)}`,
                description: item.description || `${item.claim_type || ''} - ${item.claimed_amount ? `R${item.claimed_amount.toLocaleString()}` : ''}`.trim(),
                status: status === 'draft' ? 'Draft' : (status === 'under_review' ? 'Under Review' : 'Submitted'),
                assignee: item.claimant || item.responsible_party || 'Contractor',
                submittedBy: item.claimant || item.created_by_name || 'Contractor',
                toWhom: item.decision_maker || 'Principal Agent',
                dueDate: item.submission_deadline || item.response_due_date,
                createdAt: item.created_at || item.claim_date,
                daysLeft: daysRemaining(item.submission_deadline || item.response_due_date),
                url: `/claims/${item.id}`,
                priority: status === 'draft' ? 'normal' : 'critical',
                icon: 'Scale'
              });
            }
          });

          // Meeting Minutes - open action items
          const meetingItems = Array.isArray(meetingMinutesRes.data) ? meetingMinutesRes.data : (meetingMinutesRes.data?.items || []);
          meetingItems.forEach(meeting => {
            // Check for open action items in each meeting
            const actions = meeting.action_items || [];
            actions.forEach(action => {
              const status = action.status?.toLowerCase();
              if (status === 'open' || status === 'in_progress' || status === 'pending') {
                const isOverdue = isOverdueSAST(action.due_date);
                allItems.push({
                  id: `${meeting.id}-${action.id || action.action_number}`,
                  type: 'meeting_action',
                  typeLabel: 'Meeting Action',
                  title: action.description || action.action || `Action from ${meeting.meeting_number || 'Meeting'}`,
                  description: `Meeting: ${meeting.title || meeting.meeting_number || 'Minutes'}`,
                  status: isOverdue ? 'Overdue' : 'Open',
                  assignee: action.assigned_to || action.responsible_party || 'Unassigned',
                  submittedBy: meeting.chairperson || meeting.created_by_name || 'Meeting Chair',
                  toWhom: action.assigned_to || action.responsible_party || 'Unassigned',
                  dueDate: action.due_date,
                  createdAt: meeting.meeting_date || meeting.created_at,
                  daysLeft: daysRemaining(action.due_date),
                  url: `/meeting-minutes/${meeting.id}`,
                  priority: isOverdue ? 'overdue' : 'normal',
                  icon: 'FileCheck'
                });
              }
            });
          });

          // Sort by priority (overdue first, then critical, then by due date)
          allItems.sort((a, b) => {
            const priorityOrder = { overdue: 0, critical: 1, normal: 2 };
            if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
              return priorityOrder[a.priority] - priorityOrder[b.priority];
            }
            // Then by due date (earliest first)
            if (a.dueDate && b.dueDate) {
              return new Date(a.dueDate) - new Date(b.dueDate);
            }
            return 0;
          });

          setAwaitingItems(allItems);
        } catch (error) {
          console.error('Failed to load awaiting items:', error);
          toast.error('Failed to load awaiting items');
        } finally {
          setAwaitingLoading(false);
        }
      };
      loadAwaitingItems();
    }
  }, [activeTab, projectId, awaitingRefreshKey]);

  // Filter helper functions
  const getFilterName = (filter) => {
    const filterNames = {
      'open': 'Open Items',
      'draft': 'Draft Items',
      'submitted': 'Submitted Items',
      'pending': 'Pending Items',
      'overdue': 'Overdue Items',
      'awaiting_action': 'Awaiting Action',
      'all': null
    };
    return filterNames[filter] || null;
  };

  const clearFilter = () => {
    setUrlFilter('');
    // Update URL without filter
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('filter');
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  };

  // Filtered notices with local filters
  const allNotices = events.filter(e => e.notice_id);
  const filteredNotices = allNotices.filter(event => {
    // Search filter
    if (noticeSearch) {
      const search = noticeSearch.toLowerCase();
      const matchesSearch = 
        event.event_type?.toLowerCase().includes(search) ||
        event.narrative?.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }
    
    // Status filter (local)
    if (noticeStatusFilter !== 'all') {
      if (noticeStatusFilter === 'draft' && event.notice_status !== 'Draft') return false;
      if (noticeStatusFilter === 'submitted' && event.notice_status !== 'Submitted') return false;
      if (noticeStatusFilter === 'determined' && event.notice_status !== 'Determined') return false;
    }
    
    // URL filter
    if (urlFilter && urlFilter !== 'all') {
      if (urlFilter === 'draft') {
        return event.notice_status === 'Draft';
      }
      if (urlFilter === 'submitted') {
        return event.notice_status === 'Submitted';
      }
      if (urlFilter === 'overdue') {
        const responseDate = event.notice_response_due;
        return responseDate && new Date(responseDate) < new Date() && event.notice_status !== 'Determined';
      }
    }
    
    return true;
  });

  // Filtered claims with local filters - use claims directly from eot_claims collection
  const allClaims = claims;
  const filteredClaims = allClaims.filter(claim => {
    // Search filter
    if (claimSearch) {
      const search = claimSearch.toLowerCase();
      const matchesSearch = 
        claim.claim_number?.toLowerCase().includes(search) ||
        claim.generated_content?.toLowerCase().includes(search) ||
        claim.edited_content?.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }
    
    // Status filter (local) - normalize status comparison
    const claimStatus = (claim.status || 'draft').toLowerCase();
    if (claimStatusFilter !== 'all') {
      if (claimStatusFilter === 'draft' && claimStatus !== 'draft') return false;
      if (claimStatusFilter === 'submitted' && claimStatus !== 'submitted') return false;
      if (claimStatusFilter === 'under_review' && claimStatus !== 'under_review') return false;
      if (claimStatusFilter === 'approved' && claimStatus !== 'approved') return false;
    }
    
    // URL filter
    if (urlFilter && urlFilter !== 'all') {
      if (urlFilter === 'draft') {
        return claimStatus === 'draft';
      }
      if (urlFilter === 'submitted') {
        return claimStatus === 'submitted';
      }
      if (urlFilter === 'pending') {
        return claimStatus === 'pending' || claimStatus === 'under_review';
      }
    }
    
    return true;
  });

  // Get unique event types for filter dropdown
  const eventTypes = [...new Set(events.map(e => e.event_type).filter(Boolean))];

  useEffect(() => {
    loadProject();
    // Set this project as the selected project in context (for dashboard)
    if (projectId) {
      setSelectedProjectId(projectId);
    }
  }, [projectId, setSelectedProjectId]);

  // Load clauses when project data is available (fixes race condition)
  useEffect(() => {
    if (project?.contract_type) {
      loadClauses();
    }
  }, [project?.contract_type, project?.custom_contract_rules?.custom_clauses]);

  const loadProject = async (retryCount = 0) => {
    try {
      const response = await api.projects.getById(projectId);
      setProject(response.data.project);
      
      // Fetch events from the enriched endpoint (includes compliance data)
      try {
        const eventsRes = await api.events.getByProject(projectId);
        setEvents(eventsRes.data || []);
      } catch (eventsError) {
        console.error('Failed to load enriched events, falling back:', eventsError);
        setEvents(response.data.events || []);
      }
      
      // Load claims directly from eot_claims collection
      try {
        const claimsRes = await api.get(`/projects/${projectId}/claims`).catch(() => ({ data: [] }));
        setClaims(claimsRes.data || []);
      } catch (claimsError) {
        console.error('Failed to load claims:', claimsError);
        setClaims([]);
      }
      
      // Load professional and internal teams for dropdowns
      try {
        const [profTeamRes, intTeamRes] = await Promise.all([
          api.get(`/projects/${projectId}/professional-team`).catch(() => ({ data: [] })),
          api.get(`/projects/${projectId}/internal-team`).catch(() => ({ data: [] }))
        ]);
        setProfessionalTeam(profTeamRes.data || []);
        setInternalTeam(intTeamRes.data || []);
      } catch (teamError) {
        console.error('Failed to load teams:', teamError);
      }
      setLoading(false);
    } catch (error) {
      const status = error.response?.status;
      // Retry on transient errors (network, 500, 502, 503, timeout) up to 2 times
      if ((!status || status >= 500) && retryCount < 2) {
        const delay = (retryCount + 1) * 1500;
        setTimeout(() => loadProject(retryCount + 1), delay);
        return; // Keep loading=true while retrying
      }
      // Only show error after retries exhausted or on definitive 4xx
      if (status !== 404) {
        toast.error('Failed to load project. Please refresh the page.');
      }
      setLoading(false);
    }
  };

  const loadClauses = async () => {
    try {
      const contractType = project?.contract_type || 'JBCC';
      const hasCustomClauses = project?.custom_contract_rules?.custom_clauses && Object.keys(project.custom_contract_rules.custom_clauses).length > 0;
      
      let response;
      if (hasCustomClauses) {
        response = await api.contracts.getEffectiveClauses(projectId, 'notice');
      } else {
        response = await api.contracts.getClauses(contractType, 'notice');
      }
      setClauseOptions(response.data);
      // Also load contract rules for terminology
      const rulesRes = await api.contracts.getRules(contractType);
      setContractRules(rulesRes.data);
    } catch (error) {
      // Fallback to JBCC if contract-specific loading fails
      try {
        const response = await api.jbcc.getNoticeClauses();
        setClauseOptions(response.data);
      } catch {
        console.error('Failed to load clauses');
      }
    }
  };

  // Load Variation Orders
  const loadVariationOrders = async () => {
    try {
      const response = await api.get(`/projects/${projectId}/variation-orders`);
      const data = response.data;
      if (Array.isArray(data)) {
        setVariationOrders(data);
      } else if (data && Array.isArray(data.variation_orders)) {
        setVariationOrders(data.variation_orders);
      } else {
        setVariationOrders([]);
      }
    } catch (error) {
      console.error('Failed to load variation orders:', error);
      setVariationOrders([]);
    }
  };

  // Delete Variation Order
  const handleSaveContractRules = async () => {
    setSavingRules(true);
    try {
      await api.put(`/projects/${projectId}`, { custom_contract_rules: pendingRules });
      setContractRules(pendingRules);
      toast.success('Contract rules updated');
      setEditContractDialogOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save contract rules');
    } finally {
      setSavingRules(false);
    }
  };


  const handleDeleteVO = async (voId, voNumber, e) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete ${voNumber}? This action cannot be undone.`)) {
      return;
    }
    try {
      await api.delete(`/variation-orders/${voId}`);
      toast.success('Variation Order deleted');
      setSelectedVO(null);
      loadVariationOrders();
    } catch (error) {
      console.error('Failed to delete VO:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete Variation Order');
    }
  };

  // Load Meeting Minutes
  const loadMeetingMinutes = async () => {
    try {
      const response = await api.get(`/projects/${projectId}/meeting-minutes`);
      const data = response.data;
      if (Array.isArray(data)) {
        setMeetingMinutes(data);
      } else if (data && Array.isArray(data.minutes)) {
        setMeetingMinutes(data.minutes);
      } else if (data && Array.isArray(data.meeting_minutes)) {
        setMeetingMinutes(data.meeting_minutes);
      } else {
        setMeetingMinutes([]);
      }
    } catch (error) {
      console.error('Failed to load meeting minutes:', error);
      setMeetingMinutes([]);
    }
  };

  // Load Shop Drawings
  const loadShopDrawings = async () => {
    try {
      const response = await api.get(`/projects/${projectId}/shop-drawings`);
      // Ensure we always set an array
      const data = response.data;
      if (Array.isArray(data)) {
        setShopDrawings(data);
      } else if (data && Array.isArray(data.items)) {
        setShopDrawings(data.items);
      } else if (data && Array.isArray(data.packages)) {
        setShopDrawings(data.packages);
      } else if (data && Array.isArray(data.shop_drawings)) {
        setShopDrawings(data.shop_drawings);
      } else {
        setShopDrawings([]);
      }
    } catch (error) {
      console.error('Failed to load shop drawings:', error);
      setShopDrawings([]);
    }
  };

  // Load data when switching tabs
  useEffect(() => {
    if (activeTab === 'vos') {
      loadVariationOrders();
    } else if (activeTab === 'minutes') {
      loadMeetingMinutes();
    } else if (activeTab === 'shopDrawings') {
      loadShopDrawings();
    }
  }, [activeTab, projectId]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <p className="text-slate-500 font-mono">Loading project...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!project) {
    return (
      <DashboardLayout>
        <div className="p-8">
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-12 text-center">
            <AlertCircle className="h-12 w-12 text-slate-400 mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="text-xl font-barlow font-bold uppercase mb-2">Project Not Found</h3>
            <Button onClick={() => navigate('/')} className="mt-4">
              Go Back
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-slate-100 text-slate-700 border-slate-200',
      notice_created: 'bg-blue-100 text-blue-700 border-blue-200',
      submitted: 'bg-purple-100 text-purple-700 border-purple-200',
      approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
    };
    return styles[status] || styles.draft;
  };

  const openEditProject = () => {
    setEditProjectData({
      name: project.name || '',
      client_id: project.client_id || '',
      description: project.description || '',
      contract_type: project.contract_type || 'JBCC',
      contract_number: project.contract_number || '',
      project_number: project.project_number || '',
      contractor_name: project.contractor_name || '',
      location: project.location || null,
    });
    setEditProjectOpen(true);
  };

  const handleSaveProject = async () => {
    setSavingProject(true);
    try {
      await api.projects.update(projectId, editProjectData);
      toast.success('Project updated successfully');
      setEditProjectOpen(false);
      loadProjectSummary();
    } catch (err) {
      toast.error('Failed to update project');
    }
    setSavingProject(false);
  };

  const handleDeleteProject = async () => {
    if (!window.confirm(`Are you sure you want to DELETE project "${project.name}"? This action cannot be undone and will remove all associated data.`)) return;
    try {
      await api.projects.delete(projectId);
      toast.success('Project deleted');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete project');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 overflow-x-hidden" data-testid="project-detail">
        <div className="mb-8">
          <div className="flex items-start justify-between gap-3">
            <h1 className="text-2xl sm:text-4xl md:text-5xl font-barlow font-bold uppercase tracking-tight mb-2">{project.name}</h1>
            {isAdmin && (
              <div className="flex items-center gap-1.5 shrink-0 mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openEditProject}
                  className="rounded-sm text-xs h-8"
                  data-testid="edit-project-btn"
                >
                  <Edit2 className="h-3.5 w-3.5 mr-1.5" />
                  Edit Project
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDeleteProject}
                  className="rounded-sm text-xs h-8 text-red-500 hover:text-red-700 hover:bg-red-50 border-red-200"
                  data-testid="delete-project-btn"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  Delete
                </Button>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-slate-600">
            <span className="font-mono">{project.contract_type}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setPendingRules(project.custom_contract_rules || contractRules || null); setEditContractDialogOpen(true); }}
              className="rounded-sm text-xs h-7 px-2"
              data-testid="edit-contract-rules-btn"
            >
              <Edit2 className="h-3 w-3 mr-1" />
              Edit Contract Rules
            </Button>
            <span className="hidden sm:inline">•</span>
            <span>Created {new Date(project.created_at).toLocaleDateString()}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-8">
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4 md:p-6">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Total Events</p>
            <p className="text-2xl md:text-3xl font-barlow font-bold">{events.length}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4 md:p-6">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Active</p>
            <p className="text-2xl md:text-3xl font-barlow font-bold">{events.filter(e => e.status === 'draft').length}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4 md:p-6">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Notices</p>
            <p className="text-2xl md:text-3xl font-barlow font-bold">{events.filter(e => e.status === 'notice_created').length}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4 md:p-6 flex flex-col justify-center">
            <Button
              onClick={() => navigate(`/projects/${projectId}/dashboard`)}
              data-testid="view-summary-button"
              variant="outline"
              className="w-full border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-sm font-medium uppercase tracking-wide text-xs mb-3 px-2 py-2 sm:px-4"
            >
              <BarChart3 className="h-4 w-4 sm:mr-2 shrink-0" />
              <span className="hidden sm:inline">View Summary</span>
              <span className="sm:hidden">Summary</span>
            </Button>
            <Button
              onClick={() => navigate(`/projects/${projectId}/reports`)}
              data-testid="reports-button"
              variant="outline"
              className="w-full border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white rounded-sm font-medium uppercase tracking-wide text-xs px-2 py-2 sm:px-4"
            >
              <FileText className="h-4 w-4 sm:mr-2 shrink-0" />
              <span className="hidden sm:inline">Reports</span>
              <span className="sm:hidden">Reports</span>
            </Button>
          </div>
        </div>

        <ProjectTabBar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          canViewModule={canViewModule}
          project={project}
          projectId={projectId}
        />

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <ProjectOverviewTab
            projectId={projectId}
            projectSummary={projectSummary}
            summaryLoading={summaryLoading}
            loadProjectSummary={loadProjectSummary}
            setActiveTab={setActiveTab}
            events={events}
            canCreateEvents={canCreateEvents}
            canCreateRFI={canCreateRFI}
            canCreateSI={canCreateSI}
            setRfiFormOpen={setRfiFormOpen}
            awaitingItemsCount={awaitingItems.length}
            awaitingOverdueCount={awaitingItems.filter(i => i.priority === 'overdue').length}
          />
        )}

        {activeTab === 'events' && (
          <>
          <div className="flex justify-end mb-2"><WalkthroughTrigger moduleId="delay_events" /></div>
          <DelayEventsTab
            events={events}
            project={project}
            projectId={projectId}
            canCreateEvents={canCreateEvents()}
            canMarkCeased={permissions.canMarkCeased()}
            professionalTeam={professionalTeam}
            internalTeam={internalTeam}
            clauseOptions={clauseOptions}
            contractRules={contractRules}
            urlFilter={urlFilter}
            getFilterName={getFilterName}
            clearFilter={clearFilter}
            loadProject={loadProject}
          />
          </>
        )}

        {activeTab === 'notices' && (
          <>
          <div className="flex justify-end mb-2"><WalkthroughTrigger moduleId="notices" /></div>
          <NoticesTab
            allNotices={allNotices}
            filteredNotices={filteredNotices}
            noticeSearch={noticeSearch}
            setNoticeSearch={setNoticeSearch}
            noticeStatusFilter={noticeStatusFilter}
            setNoticeStatusFilter={setNoticeStatusFilter}
            urlFilter={urlFilter}
            getFilterName={getFilterName}
            clearFilter={clearFilter}
            project={project}
          />
          </>
        )}

        {activeTab === 'claims' && (
          <>
          <div className="flex justify-end mb-2"><WalkthroughTrigger moduleId="claims" /></div>
          <ClaimsTab
            allClaims={allClaims}
            filteredClaims={filteredClaims}
            claimSearch={claimSearch}
            setClaimSearch={setClaimSearch}
            claimStatusFilter={claimStatusFilter}
            setClaimStatusFilter={setClaimStatusFilter}
            urlFilter={urlFilter}
            getFilterName={getFilterName}
            clearFilter={clearFilter}
            project={project}
          />
          </>
        )}

        {activeTab === 'rfi' && (
          <>
            <div className="flex justify-between items-center mb-2">
              <Button onClick={() => navigate('/')} variant="ghost" className="text-slate-600 hover:text-slate-900" data-testid="rfi-back-button">
                Back to Dashboard
              </Button>
              <WalkthroughTrigger moduleId="rfis" />
            </div>
            
            <RFIList 
              projectId={projectId}
              onCreateClick={() => setRfiFormOpen(true)}
              onViewRFI={(rfiId) => navigate(`/projects/${projectId}/rfi/${rfiId}`)}
              initialFilter={urlFilter}
              project={project}
            />
            <RFIForm
              projectId={projectId}
              open={rfiFormOpen}
              onOpenChange={setRfiFormOpen}
              onSuccess={() => {
                setRfiFormOpen(false);
                // Trigger RFI list refresh by changing a key
              }}
            />
          </>
        )}

        {activeTab === 'si' && (
          <>
            <div className="flex justify-between items-center mb-2">
              <Button onClick={() => navigate('/')} variant="ghost" className="text-slate-600 hover:text-slate-900" data-testid="si-back-button">
                Back to Dashboard
              </Button>
              <WalkthroughTrigger moduleId="site_instructions" />
            </div>
            
            <SIList projectId={projectId} initialFilter={urlFilter} project={project} />
          </>
        )}

        {activeTab === 'professional' && (
          <>
            {/* Back Button */}
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="mb-4 text-slate-600 hover:text-slate-900"
              data-testid="professional-back-button"
            >
              ← Back to Dashboard
            </Button>
            
            <ProfessionalTeamManager projectId={projectId} canEdit={canEditTeam} />
          </>
        )}

        {activeTab === 'internal' && (
          <>
            {/* Back Button */}
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="mb-4 text-slate-600 hover:text-slate-900"
              data-testid="internal-back-button"
            >
              ← Back to Dashboard
            </Button>
            
            <InternalTeamManager projectId={projectId} canEdit={canEditTeam} />
          </>
        )}


        {activeTab === 'subcontractors' && (
          <>
            {/* Back Button */}
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="mb-4 text-slate-600 hover:text-slate-900"
              data-testid="subcontractors-back-button"
            >
              ← Back to Dashboard
            </Button>
            
            <SubContractorManager projectId={projectId} project={project} />
          </>
        )}

        {/* Enhanced Key Milestones with History Tracking - below subcontractors */}
        {activeTab === 'milestones' && (
          <div className="space-y-6">
            {/* Back Button */}
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-slate-600 hover:text-slate-900"
              data-testid="milestones-back-button"
            >
              ← Back to Dashboard
            </Button>
            
            <MilestoneDatesModule projectId={projectId} />
          </div>
        )}

        {activeTab === 'programs' && (
          <>
            <div className="flex justify-between items-center mb-2">
              <Button onClick={() => navigate('/')} variant="ghost" className="text-slate-600 hover:text-slate-900" data-testid="programs-back-button">
                Back to Dashboard
              </Button>
              <WalkthroughTrigger moduleId="programs" />
            </div>
            
            <ProgramManager projectId={projectId} />
          </>
        )}

        {activeTab === 'drawings' && (
          <>
            <div className="flex justify-between items-center mb-2">
              <Button onClick={() => navigate('/')} variant="ghost" className="text-slate-600 hover:text-slate-900" data-testid="drawings-back-button">
                ← Back to Dashboard
              </Button>
              <WalkthroughTrigger moduleId="drawings" />
            </div>
            
            <DrawingRegister projectId={projectId} initialFilter={urlFilter} project={project} />
          </>
        )}

        {activeTab === 'vos' && (
          <>
          <div className="flex justify-end mb-2"><WalkthroughTrigger moduleId="variation_orders" /></div>
          <VariationOrdersTab
            projectId={projectId}
            variationOrders={variationOrders}
            selectedVO={selectedVO}
            setSelectedVO={setSelectedVO}
            handleDeleteVO={handleDeleteVO}
            project={project}
          />
          </>
        )}

        {activeTab === 'minutes' && (
          <>
          <div className="flex justify-end mb-2"><WalkthroughTrigger moduleId="meeting_minutes" /></div>
          <MeetingMinutesTab
            projectId={projectId}
            meetingMinutes={meetingMinutes}
            selectedMinute={selectedMinute}
            setSelectedMinute={setSelectedMinute}
            project={project}
          />
          </>
        )}

        {activeTab === 'shopDrawings' && (
          <>
          <div className="flex justify-end mb-2"><WalkthroughTrigger moduleId="shop_drawings" /></div>
          <ShopDrawingsTab
            projectId={projectId}
            shopDrawings={shopDrawings}
            selectedShopDrawing={selectedShopDrawing}
            setSelectedShopDrawing={setSelectedShopDrawing}
            project={project}
            onRefresh={loadShopDrawings}
          />
          </>
        )}

        {/* Awaiting Items Tab */}
        {activeTab === 'awaiting' && (
          <AwaitingItemsTab
            awaitingItems={awaitingItems}
            awaitingLoading={awaitingLoading}
            awaitingModuleFilter={awaitingModuleFilter}
            setAwaitingModuleFilter={setAwaitingModuleFilter}
            awaitingPriorityFilter={awaitingPriorityFilter}
            setAwaitingPriorityFilter={setAwaitingPriorityFilter}
            setAwaitingRefreshKey={setAwaitingRefreshKey}
            setActiveTab={setActiveTab}
          />
        )}
      </div>

      <EditContractRulesDialog
        open={editContractDialogOpen}
        onOpenChange={setEditContractDialogOpen}
        pendingRules={pendingRules}
        setPendingRules={setPendingRules}
        handleSaveContractRules={handleSaveContractRules}
        savingRules={savingRules}
        project={project}
      />

      <EditProjectDialog
        open={editProjectOpen}
        onOpenChange={setEditProjectOpen}
        editProjectData={editProjectData}
        setEditProjectData={setEditProjectData}
        handleSaveProject={handleSaveProject}
        savingProject={savingProject}
        project={project}
      />

    </DashboardLayout>
  );
}